import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import {
  Search,
  FileText,
  Eye,
  ThumbsUp,
  ChevronRight,
  AlertCircle,
  Rocket,
  Monitor,
  CreditCard,
  Laptop,
  Wifi,
  RefreshCw,
  AlertTriangle,
  HelpCircle,
  Clock,
  FolderOpen,
  FilePlus,
} from 'lucide-react';
import { knowledgeApi, KnowledgeCategory, Article } from '@/api/endpoints';
import { useAuthStore } from '@/stores/authStore';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import PageHero from '@/components/ui/PageHero';
import { canManageKnowledgeArticles } from './permissions';
import {
  buildKnowledgeSearchSuggestions,
  rankKnowledgeArticles,
} from './searchSuggestions';

interface CategoryDef {
  slug: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const CATEGORIES: CategoryDef[] = [
  {
    slug: 'getting-started',
    title: 'Введение в TechPanel',
    description: 'Что это за система, как в ней ориентироваться и с чего начать работу',
    icon: <Rocket className="h-6 w-6 text-[#a5b4ff]" />,
  },
  {
    slug: 'club-management',
    title: 'Admin View',
    description: 'Главная панель, статусы ПК и ежедневный контроль клуба',
    icon: <Monitor className="h-6 w-6 text-[#a5b4ff]" />,
  },
  {
    slug: 'billing',
    title: 'Setup Wizard',
    description: 'Подготовка нового ПК и первичная настройка без лишних шагов',
    icon: <CreditCard className="h-6 w-6 text-[#a5b4ff]" />,
  },
  {
    slug: 'client-pcs',
    title: 'ПК и Club:Core Client',
    description: 'Станции, клиент, автозапуск и базовые проверки на рабочих местах',
    icon: <Laptop className="h-6 w-6 text-[#a5b4ff]" />,
  },
  {
    slug: 'network',
    title: 'Сеть и VPN',
    description: 'SignalR, WireGuard, подсети и типовые проблемы с подключением',
    icon: <Wifi className="h-6 w-6 text-[#a5b4ff]" />,
  },
  {
    slug: 'updates',
    title: 'Игры и обновления',
    description: 'Game Updater, deploy, снапшоты и безопасное обновление игр',
    icon: <RefreshCw className="h-6 w-6 text-[#a5b4ff]" />,
  },
  {
    slug: 'troubleshooting',
    title: 'Диагностика, D и UWF',
    description: 'Как понять, где сломалось, и что проверить первым делом',
    icon: <AlertTriangle className="h-6 w-6 text-[#a5b4ff]" />,
  },
  {
    slug: 'faq',
    title: 'FAQ TechPanel',
    description: 'Короткие ответы на самые частые вопросы по TechPanel Unified',
    icon: <HelpCircle className="h-6 w-6 text-[#a5b4ff]" />,
  },
];

const SEARCH_EXAMPLES = [
  'не работает диск D',
  'подготовить новый ПК',
  'пропал клиент',
  'VPN',
  'UWF',
  'Game Updater',
];

const estimateReadingTime = (content?: string): number => {
  if (!content) return 1;
  const text = content.replace(/<[^>]*>/g, '');
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
};

const normalizeCategorySlug = (slug: string) => slug.replace(/_/g, '-');

const formatArticlesCount = (count: number) => {
  if (count === 1) return 'статья';
  if (count > 1 && count < 5) return 'статьи';
  return 'статей';
};

const sortPopularArticles = (articles: Article[]) =>
  [...articles].sort((left, right) => {
    const rightScore = (right.helpfulCount || 0) * 10 + (right.viewCount || 0);
    const leftScore = (left.helpfulCount || 0) * 10 + (left.viewCount || 0);
    return rightScore - leftScore;
  });

const KnowledgeBasePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const { user } = useAuthStore();
  const canManageKnowledge = canManageKnowledgeArticles(user);
  const returnTo = `${location.pathname}${location.search}`;

  const currentSearch = searchParams.get('search') || '';
  const currentCategoryId = searchParams.get('category') || '';
  const currentCategorySlug = normalizeCategorySlug(searchParams.get('cat') || '');

  const { data: apiCategories, isLoading: loadingCategories } = useQuery({
    queryKey: ['knowledge-categories'],
    queryFn: knowledgeApi.getCategories,
  });

  const { data: articlesData, isLoading: loadingArticles } = useQuery({
    queryKey: ['knowledge-all-articles'],
    queryFn: () => knowledgeApi.getArticles({ limit: 200 }),
    staleTime: 5 * 60 * 1000,
  });

  const categoryBySlug: Record<string, KnowledgeCategory> = {};
  const categoryById: Record<string, KnowledgeCategory> = {};
  if (apiCategories) {
    apiCategories.forEach((category: KnowledgeCategory) => {
      categoryBySlug[category.slug] = category;
      categoryById[category.id] = category;
    });
  }

  const activeCategoryId =
    currentCategoryId || (currentCategorySlug ? categoryBySlug[currentCategorySlug]?.id : '') || '';

  const allArticles = useMemo(() => articlesData?.data || [], [articlesData]);
  const selectedCategory = activeCategoryId ? categoryById[activeCategoryId] : undefined;
  const scopedArticles = useMemo(() => {
    if (!activeCategoryId) {
      return allArticles;
    }

    return allArticles.filter((article: Article) => article.category?.id === activeCategoryId);
  }, [allArticles, activeCategoryId]);

  const rankedResults = useMemo(
    () =>
      rankKnowledgeArticles({
        query: currentSearch,
        articles: scopedArticles,
        maxResults: 30,
      }) as Article[],
    [currentSearch, scopedArticles],
  );

  const isFiltered = !!currentSearch || !!activeCategoryId;
  const bestMatch = currentSearch ? rankedResults[0] : undefined;
  // Часть Б: статьи, подходящие клубу (по паспорту), всплывают вверх.
  // При активном текстовом поиске порядок отдаём релевантности запроса —
  // там бейдж всё равно подсвечивает совпадения.
  const matchedFirst = (list: Article[]) =>
    [...list].sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0));
  const visibleArticles = currentSearch
    ? rankedResults
    : activeCategoryId
      ? matchedFirst(scopedArticles)
      : matchedFirst(sortPopularArticles(allArticles)).slice(0, 10);

  const articleCountsBySlug = allArticles.reduce<Record<string, number>>((acc, article: Article) => {
    const slug = article.category?.slug;
    if (slug) {
      acc[slug] = (acc[slug] || 0) + 1;
    }
    return acc;
  }, {});

  const suggestions = buildKnowledgeSearchSuggestions({
    query: searchInput,
    articles: scopedArticles,
    phrases: CATEGORIES.map((category) => category.title),
  });

  const showSuggestions =
    isSearchOpen &&
    searchInput.trim().length > 0 &&
    (suggestions.phrases.length > 0 || suggestions.articles.length > 0);

  // Show CATEGORIES (hardcoded predefined) first, then any DB category
  // that the admin created or renamed which doesn't match a hardcoded slug.
  // Without this fallback those categories looked "empty" or invisible.
  const enrichedCategories = useMemo(() => {
    const predefined = CATEGORIES.map((category) => {
      const apiCategory = categoryBySlug[category.slug];
      return {
        ...category,
        articleCount: Math.max(apiCategory?.articleCount ?? 0, articleCountsBySlug[category.slug] ?? 0),
      };
    });
    const knownSlugs = new Set(CATEGORIES.map((c) => c.slug));
    const extras = (apiCategories || [])
      .filter((c: KnowledgeCategory) => c?.slug && !knownSlugs.has(c.slug))
      .map((c: KnowledgeCategory) => ({
        slug: c.slug,
        title: c.name || c.slug,
        description: (c as any).description || '',
        icon: <FolderOpen className="h-6 w-6 text-[#a5b4ff]" />,
        articleCount: Math.max((c as any).articleCount ?? 0, articleCountsBySlug[c.slug] ?? 0),
      }));
    // Показываем только категории, в которых у клиента есть ВИДИМЫЕ
    // (публичные) статьи. Иначе вылазят пустые дубли — внутренние
    // категории (internal-*) и предзаданные без статей выглядят как
    // пустые повторы публичных.
    return [...predefined, ...extras].filter(
      (c) => (articleCountsBySlug[c.slug] ?? 0) > 0,
    );
  }, [apiCategories, articleCountsBySlug, categoryBySlug]);

  const buildParams = (nextSearch: string, nextCategory?: { id?: string; slug?: string }) => {
    const params = new URLSearchParams();
    const trimmed = nextSearch.trim();

    if (trimmed) {
      params.set('search', trimmed);
    }

    if (nextCategory?.id) {
      params.set('category', nextCategory.id);
    } else if (nextCategory?.slug) {
      params.set('cat', nextCategory.slug);
    }

    return params;
  };

  useEffect(() => {
    setSearchInput(currentSearch);
  }, [currentSearch]);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (!searchRef.current?.contains(event.target as Node)) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSearchParams(
      buildParams(
        searchInput,
        activeCategoryId
          ? { id: activeCategoryId }
          : currentCategorySlug
            ? { slug: currentCategorySlug }
            : undefined,
      ),
    );
    setIsSearchOpen(false);
  };

  const handleCategoryClick = (slug: string) => {
    const apiCategory = categoryBySlug[slug];
    setSearchParams(
      buildParams(currentSearch, apiCategory ? { id: apiCategory.id } : { slug }),
    );
    setIsSearchOpen(false);
  };

  const clearFilters = () => {
    setSearchParams({});
    setSearchInput('');
    setIsSearchOpen(false);
  };

  const applyPhraseSuggestion = (value: string) => {
    setSearchInput(value);
    setSearchParams(
      buildParams(
        value,
        activeCategoryId
          ? { id: activeCategoryId }
          : currentCategorySlug
            ? { slug: currentCategorySlug }
            : undefined,
      ),
    );
    setIsSearchOpen(false);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setIsSearchOpen(false);
    }
  };

  return (
    <div className="animate-fade-in space-y-6 md:space-y-8">
      <PageHero
        eyebrow="CLUB:CORE · БАЗА ЗНАНИЙ"
        title="База знаний"
        subtitle="Сначала выбирай похожую статью из подсказок. Если жмёшь Enter, откроются самые близкие по смыслу материалы."
        ghost="DOCS"
      />

      {canManageKnowledge && (
        <div className="flex justify-end">
          <Link to="/knowledge/new" className="kb-create-article-button">
            <FilePlus className="h-4 w-4" />
            Создать статью
          </Link>
        </div>
      )}

      <section className="cc-glass rounded-[24px] p-5 md:p-8">
        <div className="mx-auto max-w-4xl space-y-5">
          <div ref={searchRef} className="relative">
            <form onSubmit={handleSearchSubmit} className="kb-search-bar">
              <Search className="h-5 w-5 text-zinc-500" />
              <input
                type="text"
                value={searchInput}
                onChange={(event) => {
                  setSearchInput(event.target.value);
                  setIsSearchOpen(true);
                }}
                onFocus={() => {
                  if (searchInput.trim()) {
                    setIsSearchOpen(true);
                  }
                }}
                onKeyDown={handleSearchKeyDown}
                placeholder="Например: не работает диск D, как подготовить новый ПК, пропал клиент..."
                className="kb-search-bar-input"
              />
              <button type="submit" className="kb-search-bar-button">
                Найти
              </button>
            </form>

            {showSuggestions && (
              <div className="kb-search-dropdown">
                {suggestions.phrases.length > 0 && (
                  <div className="kb-search-dropdown-section">
                    <div className="kb-search-dropdown-title">Подсказки</div>
                    <div className="kb-search-dropdown-list">
                      {suggestions.phrases.map((suggestion) => (
                        <button
                          key={suggestion.id}
                          type="button"
                          className="kb-search-dropdown-item"
                          onClick={() => applyPhraseSuggestion(suggestion.value)}
                        >
                          <div className="kb-search-dropdown-item-icon">
                            <Search className="h-4 w-4" />
                          </div>
                          <div className="kb-search-dropdown-item-body">
                            <div className="kb-search-dropdown-item-title">{suggestion.value}</div>
                            <div className="kb-search-dropdown-item-meta">Быстрый поисковый запрос</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {suggestions.articles.length > 0 && (
                  <div className="kb-search-dropdown-section">
                    <div className="kb-search-dropdown-title">Похожие статьи</div>
                    <div className="kb-search-dropdown-list">
                      {suggestions.articles.map((article) => (
                        <button
                          key={article.id}
                          type="button"
                          className="kb-search-dropdown-item"
                          onClick={() => {
                            setIsSearchOpen(false);
                            navigate(`/knowledge/${article.slug}`, {
                              state: { from: returnTo },
                            });
                          }}
                        >
                          <div className="kb-search-dropdown-item-icon">
                            <FileText className="h-4 w-4" />
                          </div>
                          <div className="kb-search-dropdown-item-body">
                            <div className="kb-search-dropdown-item-title">{article.title}</div>
                            <div className="kb-search-dropdown-item-meta">
                              {article.categoryName || 'Статья'}
                            </div>
                            {article.excerpt && (
                              <div className="kb-search-dropdown-item-excerpt">{article.excerpt}</div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 text-xs md:text-sm text-[#9aa3bd]">
            {SEARCH_EXAMPLES.map((example) => (
              <button
                key={example}
                type="button"
                onClick={() => applyPhraseSuggestion(example)}
                className="kb-search-example rounded-full border border-line bg-fill-2 px-3 py-1.5 transition-colors hover:border-line-hi hover:text-fg-primary"
              >
                {example}
              </button>
            ))}
          </div>
        </div>
      </section>

      {isFiltered && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {currentSearch && (
            <span className="rounded-full border border-line bg-fill-2 px-3 py-1.5 text-[#e7eaf3]">
              Поиск: {currentSearch}
            </span>
          )}
          {selectedCategory && (
            <span className="rounded-full border border-line bg-fill-2 px-3 py-1.5 text-[#e7eaf3]">
              Категория: {selectedCategory.name}
            </span>
          )}
          <button type="button" onClick={clearFilters} className="text-[#a5b4ff] hover:underline">
            Сбросить всё
          </button>
        </div>
      )}

      {currentSearch && bestMatch && (
        <Card className="cc-glass">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0 flex-1">
              <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#a5b4ff]">
                Лучшее совпадение
              </div>
              <h2 className="cc-display text-lg md:text-xl font-bold text-[#e7eaf3]">{bestMatch.title}</h2>
              {bestMatch.excerpt && (
                <p className="mt-2 text-sm md:text-base text-[#9aa3bd]">{bestMatch.excerpt}</p>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-[#727b96]">
                {bestMatch.category && (
                  <span className="rounded-full border border-line bg-fill-2 px-3 py-1 text-[#a5b4ff]">
                    {bestMatch.category.name}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {estimateReadingTime(bestMatch.content || bestMatch.excerpt)} мин
                </span>
                <span className="flex items-center gap-1">
                  <Eye className="h-3 w-3" />
                  {bestMatch.viewCount}
                </span>
              </div>
            </div>
            <Link
              to={`/knowledge/${bestMatch.slug}`}
              state={{ from: returnTo }}
              className="btn-primary whitespace-nowrap"
            >
              Открыть статью
            </Link>
          </div>
        </Card>
      )}

      {!currentSearch && !activeCategoryId && (
        <section>
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 className="cc-display text-base md:text-lg font-bold text-[#e7eaf3]">Категории</h2>
              <p className="mt-1 text-sm text-[#9aa3bd]">
                Выбирай раздел, если хочешь быстро сузить поиск без ввода текста.
              </p>
            </div>
          </div>

          {loadingCategories ? (
            <LoadingSpinner text="Загрузка категорий..." />
          ) : (
            <div className="grid gap-3 md:gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
              {enrichedCategories.map((category) => (
                <button
                  key={category.slug}
                  type="button"
                  onClick={() => handleCategoryClick(category.slug)}
                  className="cc-glass group rounded-2xl p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-line"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-line bg-fill-2 text-[#a5b4ff]">
                      {category.icon}
                    </div>
                    <ChevronRight className="mt-1 h-4 w-4 text-[#727b96] transition-colors group-hover:text-[#a5b4ff]" />
                  </div>
                  <h3 className="mt-4 text-sm font-semibold text-[#e7eaf3]">{category.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-[#9aa3bd]">{category.description}</p>
                  <div className="mt-4 text-xs font-medium text-[#727b96]">
                    {category.articleCount} {formatArticlesCount(category.articleCount)}
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      <section>
        <div className="mb-4 flex items-end justify-between gap-3">
          <div>
            <h2 className="cc-display text-base md:text-lg font-bold text-[#e7eaf3]">
              {currentSearch
                ? 'Самые похожие статьи'
                : activeCategoryId
                  ? 'Статьи по выбранной категории'
                  : 'Популярные статьи'}
            </h2>
            <p className="mt-1 text-sm text-[#9aa3bd]">
              {currentSearch
                ? `Найдено ${visibleArticles.length} ${formatArticlesCount(visibleArticles.length)} по твоему запросу`
                : activeCategoryId
                  ? 'Показываю материалы только из выбранного раздела'
                  : 'Сначала самые полезные и часто открываемые материалы'}
            </p>
          </div>
        </div>

        {loadingArticles ? (
          <LoadingSpinner text="Загрузка статей..." />
        ) : visibleArticles.length > 0 ? (
          <div className="space-y-3">
            {visibleArticles.map((article: Article) => {
              const readTime = estimateReadingTime(article.content || article.excerpt);
              return (
                <Link
                  key={article.id}
                  to={`/knowledge/${article.slug}`}
                  state={{ from: returnTo }}
                  className={`cc-glass group block rounded-2xl p-4 md:p-5 no-underline hover:no-underline transition-all duration-200 hover:-translate-y-0.5 hover:border-line${
                    article.matchedForClub ? ' ring-1 ring-[#a5b4ff]/60 border-[#a5b4ff]/40' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 md:gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <FileText className="h-4 w-4 flex-shrink-0 text-[#727b96] group-hover:text-[#a5b4ff]" />
                        <h3 className="text-sm font-semibold text-[#e7eaf3] group-hover:text-fg-primary">
                          {article.title}
                        </h3>
                        {article.matchedForClub && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-[#a5b4ff]/40 bg-[#a5b4ff]/10 px-2 py-0.5 text-[11px] font-medium text-[#a5b4ff]">
                            🎯 подходит вашему клубу
                          </span>
                        )}
                      </div>
                      {article.excerpt && (
                        <p className="mt-2 text-sm leading-6 text-[#9aa3bd]">{article.excerpt}</p>
                      )}
                      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-[#727b96]">
                        {article.category && (
                          <span className="rounded-full border border-line bg-fill-2 px-3 py-1 font-medium text-[#a5b4ff]">
                            {article.category.name}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {readTime} мин
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="h-3 w-3" />
                          {article.viewCount}
                        </span>
                        <span className="flex items-center gap-1">
                          <ThumbsUp className="h-3 w-3" />
                          {article.helpfulCount}
                        </span>
                        <span className="hidden sm:inline">
                          {format(new Date(article.updatedAt), 'dd MMM yyyy', { locale: ru })}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="mt-1 h-4 w-4 flex-shrink-0 text-[#727b96] group-hover:text-[#a5b4ff]" />
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <EmptyState
            icon={<AlertCircle className="h-8 w-8 text-zinc-500" />}
            title="Подходящих статей не нашлось"
            description={
              currentSearch
                ? 'Попробуй переформулировать запрос или открой подходящую категорию ниже.'
                : 'Скоро здесь появятся полезные статьи'
            }
          />
        )}
      </section>
    </div>
  );
};

export default KnowledgeBasePage;
